import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  PartialWithFieldValue,
  QueryFilterConstraint,
  WriteBatch,
} from 'firebase/firestore'
import {
  and,
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  deleteField,
  doc,
  documentId,
  getCountFromServer,
  increment,
  limit,
  or,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { ObservableModelDocument } from '../../firestore-mobx/model'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
  documentSnapshots,
  modelItemStream,
  modelListStream,
} from '../../firestore-mobx/stream'

import { DateTime } from 'luxon'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsInChunksWithError,
  getDocsWithError,
  getDocsWithPartitionedQuery,
  getDocWithError,
  setDocWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { RoomState } from '../../models/RoomState'
import type { RoomStateDemoType } from '../../types'
import { DocumentDoesNotExistError } from '../../types'
import type { FirestoreRoomState } from './schema'
import { schema, writeSchema } from './schema'
import { createBatchGet } from '../../util/batch-get'

export * from './schema'

export interface RoomStateObservableModelDocument extends ObservableModelDocument<
  RoomState,
  FirestoreRoomState
> {}

const converter: FirestoreDataConverter<FirestoreRoomState> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreRoomState>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore
): CollectionReference<FirestoreRoomState> => {
  return collection(firestore, 'room_state').withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  id: string
): DocumentReference<FirestoreRoomState, DocumentData> => {
  return doc(getColRef(firestore), id)
}

export const roomStateSetGroupLeaders = async (
  firestore: Firestore,
  roomId: string,
  userIds: string[]
) => {
  const ref = getDocRef(firestore, roomId)

  return updateDocWithError(
    ref,
    {
      groupLeaderUserIds: userIds,
    },
    'RoomStateSetGroupLeadersError'
  )
}

export const roomStateAddGroupLeader = async (
  firestore: Firestore,
  roomId: string,
  userId: string
) => {
  const ref = getDocRef(firestore, roomId)

  return updateDocWithError(
    ref,
    {
      groupLeaderUserIds: arrayUnion(userId),
    },
    'RoomStateAddGroupLeaderError'
  )
}

export const roomStateUpdateActiveSlide = async (
  firestore: Firestore,
  params: {
    roomId: string
    slideIndex: number
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  return updateDocWithError(
    ref,
    {
      activeSlide: params.slideIndex,
      activeSlideChangedAt: serverTimestamp(),
      activeExhibitId: null,
    },
    'RoomStateUpdateActiveSlideError'
  )
}

// perform the action associated with the slide change again
export const roomStateUpdatePumpSlide = async (
  firestore: Firestore,
  params: {
    roomId: string
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  return updateDocWithError(
    ref,
    {
      pumpSlide: increment(1),
      updatedAt: serverTimestamp(),
    },
    'RoomStateUpdateActiveSlideError'
  )
}

export const roomStateTouchActiveSlideChangedAt = async (
  firestore: Firestore,
  roomId: string
) => {
  const ref = getDocRef(firestore, roomId)

  return updateDocWithError(
    ref,
    {
      activeSlideChangedAt: serverTimestamp(),
    },
    'TouchActiveSlideChangedAtError'
  )
}

// perform the action associated with the slide change again
export const roomStateUpdatePumpSlideWithActiveSlideChangedAt = async (
  firestore: Firestore,
  params: {
    roomId: string
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  return updateDocWithError(
    ref,
    {
      pumpSlide: increment(1),
      updatedAt: serverTimestamp(),
      activeSlideChangedAt: serverTimestamp(),
    },
    'RoomStatePumpSlideWithActiveSlideChangedAtError'
  )
}

export const updateRoomState = async (
  firestore: Firestore,
  roomId: string,
  data: PartialWithFieldValue<FirestoreRoomState>
) => {
  const ref = getDocRef(firestore, roomId)

  return updateDocWithError(ref, data, 'UpdateRoomStateError')
}

export const batchUpdateRoomState = (
  firestore: Firestore,
  batch: WriteBatch,
  roomId: string,
  data: PartialWithFieldValue<FirestoreRoomState>
) => {
  const ref = getDocRef(firestore, roomId)
  return batch.update(ref, data)
}

export const ensureRoomStateAnswerObjectPosition = async (
  firestore: Firestore,
  roomId: string,
  questionId: string
) => {
  const ref = getDocRef(firestore, roomId)

  const updateData = {
    answerObjectPositions: {
      [questionId]: {
        noop: {
          container: 'noop',
          x: 0,
          y: 0,
        },
      },
    },
  }

  return updateDocWithError(ref, updateData, 'UpdateRoomStateError')
}

export const updateRoomStateAnswerObjectPosition = async (
  firestore: Firestore,
  roomId: string,
  questionId: string,
  data: {
    id: string
    container: string
    x: number
    y: number
    text?: string
  }
) => {
  const ref = getDocRef(firestore, roomId)

  const updateData = {
    [`answerObjectPositions.${questionId}.${data.id}`]: {
      container: data.container,
      x: data.x,
      y: data.y,
      text: data.text || '',
    },
    updatedAt: serverTimestamp(),
  }

  return updateDocWithError(ref, updateData, 'UpdateRoomStateError')
}

export const removeRoomStateAnswerObjectPosition = async (
  firestore: Firestore,
  roomId: string,
  questionId: string,
  id: string
) => {
  const ref = getDocRef(firestore, roomId)

  const updateData = {
    [`answerObjectPositions.${questionId}.${id}`]: deleteField(),
    updatedAt: serverTimestamp(),
  }

  return updateDocWithError(ref, updateData, 'UpdateRoomStateError')
}

export const updateRoomStateWithPumpSlide = async (
  firestore: Firestore,
  roomId: string,
  data: PartialWithFieldValue<FirestoreRoomState>
) => {
  const ref = getDocRef(firestore, roomId)

  return updateDocWithError(
    ref,
    {
      ...data,
      pumpSlide: increment(1),
      updatedAt: serverTimestamp(),
    },
    'UpdateRoomStateError'
  )
}

export const roomStateAddUser = async (
  firestore: Firestore,
  params: {
    roomId: string
    userId: string
    isGroupLeader: boolean
    batch?: ReturnType<typeof writeBatch>
    roleId?: string
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  const data: PartialWithFieldValue<FirestoreRoomState> = {
    userIds: arrayUnion(params.userId),
    updatedAt: serverTimestamp(),
  }

  if (params.isGroupLeader)
    data['groupLeaderUserIds'] = arrayUnion(params.userId)

  // if role is valid string, add it to the assignmentRoles map
  if (params.roleId)
    // @ts-expect-error -- fancy dynamic field path for writing the user id to the roles map cba on type
    data[`assignmentRoles.${params.userId}`] = params.roleId

  // delete field as empty string is not valid role assignment
  if (params.roleId === '')
    // @ts-expect-error -- fancy dynamic field path for writing the user id to the roles map cba on type
    data[`assignmentRoles.${params.userId}`] = deleteField()

  if (params.batch) return params.batch.update(ref, data)

  return updateDocWithError(ref, data, 'RoomStateAddUserError')
}

export const removeUserRole = async (
  firestore: Firestore,
  params: {
    roomId: string
    userId: string
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  const data: PartialWithFieldValue<FirestoreRoomState> = {
    [`assignmentRoles.${params.userId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  }

  return updateDocWithError(ref, data, 'RemoveUserRoleError')
}

export const roomStateAddHiddenUser = async (
  firestore: Firestore,
  params: {
    roomId: string
    userId: string
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  const data: PartialWithFieldValue<FirestoreRoomState> = {
    hiddenUserIds: arrayUnion(params.userId),
  }

  return updateDocWithError(ref, data, 'RoomStateAddHiddenUserError')
}

export const leaveRoom = async (
  firestore: Firestore,
  params: {
    roomId: string
    userId: string
    clearRole?: boolean
    batch?: ReturnType<typeof writeBatch>
  }
) => {
  const ref = getDocRef(firestore, params.roomId)

  const payload = {
    userIds: arrayRemove(params.userId),
    groupLeaderUserIds: arrayRemove(params.userId),
    updatedAt: serverTimestamp(),
    [`assignmentRoles.${params.userId}`]: params.clearRole
      ? deleteField()
      : undefined,
  }

  if (params.batch) return params.batch.update(ref, payload)

  await updateDocWithError(ref, payload, 'LeaveRoomError')
}

export const batchCreateRoomState = (
  firestore: Firestore,
  batch: WriteBatch,
  data: {
    deletable: boolean
    userIds: string[]
    groupLeaderUserIds: string[]
    scheduled?: Date
    roomStateName?: string
    assignmentId: string
    sectionId: string
    slideDeckId: string
  }
) => {
  const colRef = getColRef(firestore)
  const ref = doc(colRef)
  const roomStateName = data.roomStateName || ''

  return batch.set(ref, {
    activeSlide: null,
    assignmentId: data.assignmentId,
    groupLeaderUserIds: data.groupLeaderUserIds,
    hiddenUserIds: [],
    roomStateName: roomStateName,
    scheduledAt: data.scheduled,
    sectionId: data.sectionId,
    slideDeckId: data.slideDeckId,
    updatedAt: serverTimestamp(),
    userIds: data.userIds,
    deletable: data.deletable,
  })
}

export const createRoomState = async (
  firestore: Firestore,
  data: {
    deletable: boolean
    userId?: string
    scheduled?: Date
    name?: string
    assignmentId: string
    sectionId: string
    slideDeckId: string
  }
) => {
  const ref = getColRef(firestore)

  const roomStateName = data.name || ''

  return addDocWithError(
    ref,
    {
      activeSlide: null,
      assignmentId: data.assignmentId,
      groupLeaderUserIds: data.userId ? [data.userId] : [],
      hiddenUserIds: [],
      roomStateName: roomStateName,
      scheduledAt: data.scheduled,
      sectionId: data.sectionId,
      slideDeckId: data.slideDeckId,
      updatedAt: serverTimestamp(),
      userIds: data.userId ? [data.userId] : [],
      deletable: data.deletable,
    },
    'CreateRoomStateError'
  )
}

export const createDemoRoomState = async (
  repository: FirebaseRepository,
  data: {
    userId?: string
    scheduledAt: DateTime
    name?: string
    slideDeckId: string
    demoType?: RoomStateDemoType
  }
) => {
  const ref = getColRef(repository.firestore)

  const roomStateName = data.name || ''

  const userIds = data.userId ? [data.userId] : []

  return addDocWithError(
    ref,
    {
      activeSlide: null,
      activeSlideChangedAt: null,
      demoType: data.demoType,
      groupLeaderUserIds: userIds,
      hiddenUserIds: [],
      roomStateName: roomStateName,
      scheduledAt: data.scheduledAt.toJSDate(),
      isDemo: true,
      slideDeckId: data.slideDeckId,
      updatedAt: serverTimestamp(),
      userIds: userIds,
    },
    'CreateDemoRoomStateError'
  )
}

export const ensureDevRoomState = async (
  repository: FirebaseRepository,
  data: {
    userId: string
    name?: string
    slideDeckId: string
  }
) => {
  const roomRef = getDocRef(repository.firestore, data.slideDeckId)
  const roomStateName = data.name || ''

  const room = await fetchRoomState(repository, {
    roomStateId: data.slideDeckId,
    returnEmptyOnNotExists: true,
  })

  if (room && room.isNotEmpty) {
    if (!room.userIds.includes(data.userId)) {
      // make sure the user is added to the dev room
      await updateDocWithError(roomRef, {
        userIds: arrayUnion(data.userId),
      })
    }

    return room
  }

  const scheduledAt = DateTime.now()

  await setDocWithError(
    roomRef,
    {
      activeSlide: null,
      activeSlideChangedAt: null,
      groupLeaderUserIds: [data.userId],
      hiddenUserIds: [],
      roomStateName: roomStateName,
      scheduledAt: scheduledAt.toJSDate(),
      // MUST be false
      isDemo: false,
      slideDeckId: data.slideDeckId,
      updatedAt: serverTimestamp(),
      userIds: [data.userId],
    },
    { errorName: 'CreateDevRoomStateError' }
  )

  const newRoom = await fetchRoomState(repository, {
    roomStateId: data.slideDeckId,
    returnEmptyOnNotExists: true,
  })

  return newRoom
}

export const fetchRoomState = async (
  repository: FirebaseRepository,
  {
    roomStateId,
    returnEmptyOnNotExists = false,
  }: { roomStateId: string; returnEmptyOnNotExists?: boolean }
) => {
  const docRef = getDocRef(repository.firestore, roomStateId)

  const doc = await getDocWithError(docRef, 'FetchRoomStateError')

  if (!doc.exists()) {
    if (returnEmptyOnNotExists) return RoomState.empty(repository)
    throw new DocumentDoesNotExistError()
  }

  return convertDocumentSnapshotToModel(repository, doc, RoomState)
}

export const getRoomStatesForStudent = (
  repository: FirebaseRepository,
  {
    sectionId,
  }: {
    sectionId?: string
  } = {}
) => {
  const ref = getColRef(repository.firestore)
  const userId = repository.uid

  if (sectionId) {
    const q = query(
      ref,
      and(
        where('sectionId', '==', sectionId),
        or(
          where('userIds', 'array-contains', userId),
          where('groupLeaderUserIds', 'array-contains', userId)
        )
      )
    )
    return modelListStream(repository, q, RoomState)
  }

  const q = query(
    ref,
    or(
      where('userIds', 'array-contains', userId),
      where('groupLeaderUserIds', 'array-contains', userId)
    )
  )
  return modelListStream(repository, q, RoomState)
}

export const fetchRoomStatesForUser = async (
  repository: FirebaseRepository,
  { userId }: { userId: string }
) => {
  const ref = getColRef(repository.firestore)
  const q = query(ref, where('userIds', 'array-contains', userId))
  const snapshot = await getDocsWithError(q, 'FetchRoomStatesForUserError')

  return snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, RoomState)
  })
}

export const getRoomStates = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId?: string }
) => {
  const ref = getColRef(repository.firestore)
  const q = assignmentId
    ? query(
        ref,
        and(
          where('sectionId', '==', sectionId),
          where('assignmentId', '==', assignmentId)
        )
      )
    : query(ref, where('sectionId', '==', sectionId))

  return modelListStream(repository, q, RoomState)
}

export const fetchRoomStates = async (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const ref = getColRef(repository.firestore)
  const q = query(
    ref,
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId)
    )
  )

  const snapshot = await getDocsInChunksWithError(q, 'FetchRoomStatesError')

  return snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(repository, doc, RoomState)
  })
}

export const fetchRoomStatesByIds = async (
  repository: FirebaseRepository,
  roomIds: string[]
) => {
  if (roomIds.length === 0) return []

  const ref = getColRef(repository.firestore)

  // Use the shared batch-get utility which will split 'in' queries into
  // appropriately-sized batches and convert snapshots to models.
  return createBatchGet({
    repository,
    collectionRef: ref,
    ids: roomIds,
    modelClass: RoomState,
  })
}

export const getRoomState = (
  repository: FirebaseRepository,
  { roomStateId }: { roomStateId: string }
) => {
  const docRef = getDocRef(repository.firestore, roomStateId)

  return modelItemStream(repository, docRef, RoomState)
}

export const getRoomStatesRunning = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const timeToCheck = DateTime.now().minus({ minutes: 30 }).toJSDate()

  const q = query(ref, where('activeSlideChangedAt', '>', timeToCheck))
  return modelListStream(repository, q, RoomState)
}

export const getRoomStatesScheduled = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const timeToCheck = DateTime.now().minus({ hours: 48 }).toJSDate()

  const q = query(
    ref,
    and(
      where('activeSlide', '==', null),
      where('scheduledAt', '>', timeToCheck)
    )
  )
  return modelListStream(repository, q, RoomState)
}

export const getRoomWasGraded = (
  repository: FirebaseRepository,
  { roomStateId }: { roomStateId: string }
) => {
  const q = query(
    collection(repository.firestore, 'room_state', roomStateId, 'engagement'),
    limit(1)
  )
  return collectionSnapshots(q).map(({ docs }) => docs.length > 0)
}

export const getStudentWasGraded = (
  repository: FirebaseRepository,
  { roomStateId, userId }: { roomStateId: string; userId: string }
) => {
  const ref = doc(
    repository.firestore,
    'room_state',
    roomStateId,
    'engagement',
    userId
  )
  return documentSnapshots(ref).map((doc) => {
    return doc.exists()
  })
}

/**
 * request to start the ai processing. This is done automatically when the last user leaves
 * a room, but this will allow it to be rerun by admin
 */
export const forceAIProcessing = async (
  repository: FirebaseRepository,
  {
    roomId,
  }: {
    roomId: string
  }
) => {
  const collectionRef = getColRef(repository.firestore)
  const docRef = doc(collectionRef, roomId)
  const aiRecordsCollectionRef = collection(
    repository.firestore,
    docRef.path,
    'ai'
  )
  const aiRecordsQuery = query(
    aiRecordsCollectionRef,
    where('endedAt', '==', null)
  )
  const aiRecords = await getDocsWithError(
    aiRecordsQuery,
    'FetchForForceAIProcessingError'
  )
  if (!aiRecords.docs.length) {
    await addDocWithError(
      aiRecordsCollectionRef,
      {
        startedAt: serverTimestamp(),
        roomId: roomId,
        endedAt: null,
      },
      'ForceAIProcessingError'
    )
    return true
  }
  return false
}

/** sets active slide and active slide changes at to null */
export const roomStateReset = async (
  repository: FirebaseRepository,
  {
    roomId,
  }: {
    roomId: string
  }
) => {
  return await updateDocWithError(
    getDocRef(repository.firestore, roomId),
    {
      activeSlide: null,
      activeSlideChangedAt: null,
      activeExhibitId: null,
      roomStartedAt: null,
    },
    'RoomStateResetError'
  )
}

export const getDemoRoomStates = (repository: FirebaseRepository) => {
  const ref = getColRef(repository.firestore)
  const weekAgo = DateTime.now().toUTC().minus({ days: 7 }).toJSDate()
  const predicates: Array<QueryFilterConstraint> = [
    where('isDemo', '==', true),
    where('scheduledAt', '>', weekAgo),
  ]
  const q = query(ref, and(...predicates))
  return modelListStream(repository, q, RoomState)
}

export const adminStartDemoMeeting = async (
  repository: FirebaseRepository,
  {
    roomStateId,
  }: {
    roomStateId: string
  }
) => {
  const roomStateRef = getDocRef(repository.firestore, roomStateId)
  return updateDocWithError(
    roomStateRef,
    {
      activeSlide: 0,
      activeSlideChangedAt: new Date(),
      roomStartedAt: new Date(),
      groupLeaderUserIds: [repository.uid],
      userIds: arrayUnion(repository.uid),
    },
    'AdminStartDemoMeetingError'
  )
}

export const fetchMostRecentlyGradedRoomStatesForSlideDeck = async (
  repository: FirebaseRepository,
  {
    slideDeckId,
    limit: argLimit,
  }: {
    slideDeckId: string
    limit: number
  }
) => {
  const aiCollectionGroupRef = collectionGroup(repository.firestore, 'ai')
  const aiCollectionGroupQuery = query(
    aiCollectionGroupRef,
    where('slideDeckId', '==', slideDeckId),
    where('success', '==', true), // only successful ai records
    orderBy('endedAt', 'desc'), // most recent first
    limit(argLimit) // limit to the number of records requested
  )

  const snap = await getDocsWithError(
    aiCollectionGroupQuery,
    'FetchMostRecentlyGradedRoomStatesForSlideDeckError-AI'
  )

  const roomIds = snap.docs.map((doc) => {
    // throw obvious error if the document reference is not a room_state (our collection group query must be refactored)
    if (!doc.ref.path.startsWith('room_state/'))
      throw new Error(
        'FetchMostRecentlyGradedRoomStatesForSlideDeckError-AI: Document reference does not start with room_state/'
      )

    const roomRef = doc.ref.parent.parent // get the parent of the ai collection, which is the room_state document
    if (!roomRef)
      throw new Error(
        'FetchMostRecentlyGradedRoomStatesForSlideDeckError-AI: Room reference not found'
      )

    return roomRef.id
  })

  const docs = await getDocsWithPartitionedQuery({
    queryBuilder: (partition) =>
      query(
        collection(repository.firestore, 'room_state').withConverter(converter),
        where(documentId(), 'in', partition)
      ),
    matchArray: roomIds,
    errorName: 'FetchMostRecentlyGradedRoomStatesForSlideDeckError-RoomState',
  })

  return docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, RoomState)
  )
}

export const fetchRoomsForSectionWhereAISucceeded = async (
  repository: FirebaseRepository,
  { sectionId }: { sectionId: string }
) => {
  const ref = getColRef(repository.firestore)

  // first get all rooms for a given section
  const q = query(ref, and(where('sectionId', '==', sectionId)))
  const snapshot = await getDocsWithError(q, 'FetchRoomsForSectionError')

  // create a promise for all rooms with activeSlide to see if they have a successful ai record
  const roomIdsThatDidSucceed = new Set<string>()
  await Promise.all(
    snapshot.docs.map(async (doc) => {
      const roomState = convertDocumentSnapshotToModel(
        repository,
        doc,
        RoomState
      )

      // no users or no active slide means no meaningful ai record
      if (!roomState.data.activeSlide || !roomState.data.userIds.length)
        return Promise.resolve()

      const aiRecordsCollectionRef = collection(doc.ref, 'ai')

      return (async () => {
        const { docs } = await getDocsWithError(
          aiRecordsCollectionRef,
          'FetchAIRecordsError'
        )
        if (docs.some((aiDoc) => aiDoc.data()?.success))
          roomIdsThatDidSucceed.add(roomState.id)
      })()
    })
  )

  // of the original rooms query, return only those that have successful ai record(s)
  return snapshot.docs.filter((doc) => roomIdsThatDidSucceed.has(doc.id))
}

/**
 * There is no demo specific logic for this, the as now the rules only allow for deletion
 * of demo roomStates
 */
export const deleteDemoRoomState = async (
  repository: FirebaseRepository,
  {
    roomStateId,
  }: {
    roomStateId: string
  }
) => {
  const roomStateRef = getDocRef(repository.firestore, roomStateId)
  await deleteDocWithError(roomStateRef, 'DeleteDemoRoomStateError')
}

export const bulkCreateRoomStates = async (
  firestore: Firestore,
  data: {
    count: number
    baseData: {
      deletable: boolean
      assignmentId: string
      sectionId: string
      slideDeckId: string
      scheduledAt?: Date
    }
    roomStateNames: string[]
  }
) => {
  const batch = writeBatch(firestore)
  const colRef = getColRef(firestore)
  const createdRefs = []

  for (let i = 0; i < data.count; i++) {
    const docRef = doc(colRef)
    createdRefs.push(docRef)

    batch.set(docRef, {
      activeSlide: null,
      assignmentId: data.baseData.assignmentId,
      groupLeaderUserIds: [],
      hiddenUserIds: [],
      roomStateName: data.roomStateNames[i],
      scheduledAt: data.baseData.scheduledAt,
      sectionId: data.baseData.sectionId,
      slideDeckId: data.baseData.slideDeckId,
      updatedAt: serverTimestamp(),
      userIds: [],
      deletable: data.baseData.deletable,
    })
  }

  try {
    await batch.commit()

    // Return the created room references so they can be used by the cubit
    return createdRefs
  } catch (error) {
    throw new Error('CreateMultipleRoomStatesError')
  }
}

export const countRoomStatesInRange = async (
  repository: FirebaseRepository,
  params: { start: Date; end: Date; field?: 'scheduledAt' | 'roomStartedAt' }
): Promise<number> => {
  const field = params.field ?? 'scheduledAt'
  const ref = getColRef(repository.firestore)
  const q = query(
    ref,
    and(where(field, '>=', params.start), where(field, '<', params.end))
  )
  const snapshot = await getCountFromServer(q)
  return snapshot.data().count
}

export const bulkScheduleRoomStates = async (
  repository: FirebaseRepository,
  {
    roomStateIds,
    scheduledAt,
  }: {
    roomStateIds: string[]
    scheduledAt: Date
  }
) => {
  const roomStateRefs = roomStateIds.map((id) =>
    getDocRef(repository.firestore, id)
  )
  const batch = writeBatch(repository.firestore)
  roomStateRefs.forEach((ref) => {
    batch.update(ref, { scheduledAt })
  })
  await batch.commit()
}

/**
 *
 * @param firestore
 * @param updates can be an array of updates or an object with updates and append flag.
 * if append flag is not passed the userIds array will be replaced
 *
 * @example
 * ```ts
 * // replace userIds
 * bulkUpdateRoomUsers(firestore, [
 *  { roomId: 'room1', userIds: ['user1', 'user2'] },
 *  { roomId: 'room2', userIds: ['user3'] },
 * ])
 *
 * // append userIds
 * bulkUpdateRoomUsers(firestore, {
 *  updates: [
 *    { roomId: 'room1', userIds: ['user1', 'user2'] },
 *    { roomId: 'room2', userIds: ['user3'] },
 *  ],
 *  append: true,
 * })
 * ```
 */
const BATCH_SIZE = 500

export const unassignRoomUsers = async (
  firestore: Firestore,
  roomIds: string[]
) => {
  for (let i = 0; i < roomIds.length; i += BATCH_SIZE) {
    const chunk = roomIds.slice(i, i + BATCH_SIZE)
    const batch = writeBatch(firestore)
    for (const roomId of chunk) {
      const ref = getDocRef(firestore, roomId)
      batch.update(ref, {
        userIds: [],
        groupLeaderUserIds: [],
        assignmentRoles: {},
        hiddenUserIds: [],
        updatedAt: serverTimestamp(),
      })
    }
    await batch.commit()
  }
}

export const bulkUpdateRoomUsers = async (
  firestore: Firestore,
  updates:
    | Array<{
        roomId: string
        userIds: string[]
        append?: boolean
      }>
    | {
        // allow user to pass object with append flag
        updates: Array<{
          roomId: string
          userIds: string[]
          append?: never
        }>
        append: boolean
      }
) => {
  const batch = writeBatch(firestore)

  const userUpdates = Array.isArray(updates) ? updates : updates.updates
  const appendGlobal = !Array.isArray(updates) ? updates.append : false

  for (const update of userUpdates) {
    const ref = getDocRef(firestore, update.roomId)

    // append can either be defined as a global, or on the update level
    // must be one or the other or none, can not be on the update and global
    // enforced by the arguments
    const append = appendGlobal || ('append' in update && update.append)

    batch.update(ref, {
      userIds: !append ? update.userIds : arrayUnion(...update.userIds),
      updatedAt: serverTimestamp(),
    })
  }

  try {
    await batch.commit()
  } catch (error) {
    const err = error as Error
    throw new Error('BulkUpdateRoomUsersError: ' + err.message)
  }
}
